import {
  Array2d,
  isMultiPolygonFeature,
  isPointFeature,
  isPolygonFeature,
  mergeFeatureCollection,
} from '@cibo/core'
import buffer from '@turf/buffer'
import cleanCoords from '@turf/clean-coords'
import difference from '@turf/difference'
import { FeatureCollection, featureCollection } from '@turf/helpers'
import simplify from '@turf/simplify'
import truncate from '@turf/truncate'
import { Feature, Geometry, MultiPolygon, Point, Polygon } from 'geojson'
import {
  Dispatch,
  RefObject,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMap } from 'react-map-gl/dist/es5'
import { useMajorityCrops } from '../../../../hooks'
import { useFieldSearch } from '../../../../queries'
import { useLocalLedgerFields } from '../../../../queries/useLocalLedgerFields'
import { MapGLViewport, useViewportContext } from '../../ViewportContext'
import { GeoJSONEditHistory, GeoJSONLayerState } from '../GeometryEditorTypes'
import { useEditorOverlaps } from '../useEditorOverlaps'
import { findEditorOverlaps } from '../useEditorOverlaps/findEditorOverlaps'

export interface GeometryEditorState {
  autofixOverlaps(): void
  container: RefObject<HTMLDivElement>
  editorOverlaps: ReturnType<typeof useEditorOverlaps>
  editState: GeoJSONEditHistory
  features: FeatureCollection
  fieldPoints?: Feature<Point>[]
  fieldShapes?: Feature<Polygon | MultiPolygon>[]
  handleUndo?: () => void
  handleRedo?: () => void
  majorityCrops?: any
  resourceId?: string
  setEditState: (editState: GeoJSONEditHistory) => void
  previewFeatures: any
  setPreviewFeatures: Dispatch<SetStateAction<any>>
  resetGeometryEditor: () => void
  setViewport(bounds: MapGLViewport): void
  touched: boolean
  baseFeatureCollection?: FeatureCollection
  setBaseFeatureCollection: Dispatch<SetStateAction<FeatureCollection>>
}

export const GeometryEditorStateContext = createContext<GeometryEditorState>({
  // @ts-ignore
  container: null,
  editorOverlaps: { isReady: false, overlaps: undefined },
  editState: {
    editLayer: { features: featureCollection([]), selectedIndexes: [] },
    redoStack: [],
    undoStack: [],
    transforming: false,
  },
  features: featureCollection([]),
  setEditState: () => console.log('setEditState unset'),
  setViewport: () => console.log('setViewport unset'),
  touched: false,
  setBaseFeatureCollection: () => console.log('setBaseFeatureCollection unset'),
  resetGeometryEditor: () => console.log('resetGeometryEditor unset'),
  previewFeatures: [],
  setPreviewFeatures: () => console.log('setPreviewFeatures unset'),
})

export const GeometryEditorStateProvider: React.FC<{
  resourceId?: string
  initialFeatures?: FeatureCollection
  displayFields?: boolean
}> = ({ children, resourceId, initialFeatures, displayFields = true }) => {
  const container = useRef<HTMLDivElement>(null)
  const [baseFeatureCollection, setBaseFeatureCollection] = useState<FeatureCollection>(
    initialFeatures ? initialFeatures : featureCollection([])
  )

  const { viewport, setViewport } = useViewportContext()

  const { mapEditor: map } = useMap()

  const fieldsQuery = useMemo(() => {
    if (!displayFields) return

    const mapBounds = map?.getBounds()

    if (!mapBounds) return

    return {
      lowerLeft: mapBounds.getSouthWest().toArray() as [number, number],
      upperRight: mapBounds.getNorthEast().toArray() as [number, number],
    }
  }, [viewport])

  const localLedgerFields = useLocalLedgerFields(fieldsQuery)
  const overlapTestFields = localLedgerFields.data?.features.filter(
    ({ properties }) => properties?.resourceId !== resourceId
  )

  const fields = useFieldSearch(fieldsQuery, undefined, true)
  const viewportFields = fields.data?.features.filter(
    ({ properties }) => properties?.resourceId !== resourceId
  )

  const fieldPoints: Feature<Point>[] = []
  const fieldShapes: Feature<Polygon | MultiPolygon>[] = []
  viewportFields?.forEach(field => {
    if (isPointFeature(field)) {
      fieldPoints.push(field)
    } else if (isPolygonFeature(field) || isMultiPolygonFeature(field)) {
      fieldShapes.push(field)
    }
  })

  const [editState, setEditState] = useState<GeoJSONEditHistory>({
    editLayer: {
      features: featureCollection([]),
      selectedIndexes: [] as number[],
    },
    redoStack: [],
    undoStack: [],
    transforming: false,
  })

  const handleUndo =
    editState.undoStack.length > 0
      ? () => {
          const editLayer = editState.undoStack.shift() as GeoJSONLayerState
          setEditState({
            ...editState,
            editLayer,
            undoStack: editState.undoStack, //already mutated above
            redoStack: [editState.editLayer, ...editState.redoStack],
          })
          setPreviewFeatures(editLayer.features)
        }
      : undefined

  const handleRedo =
    editState.redoStack.length > 0
      ? () => {
          const editLayer = editState.redoStack.shift() as GeoJSONLayerState
          setEditState({
            ...editState,
            editLayer,
            undoStack: [editState.editLayer, ...editState.undoStack],
            redoStack: editState.redoStack, //already mutated above
          })
          setPreviewFeatures(editLayer.features)
        }
      : undefined

  const features = editState.editLayer.features

  const editorOverlaps = useEditorOverlaps(
    features as FeatureCollection<Polygon | MultiPolygon>,
    overlapTestFields
  )

  const majorityCrops = useMajorityCrops(
    editState.editLayer.features as FeatureCollection<Geometry, any>,
    2022
  )

  const [previewFeatures, setPreviewFeatures] = useState<FeatureCollection>(
    initialFeatures || featureCollection([])
  )

  const resetGeometryEditor = useCallback(() => {
    setEditState({
      editLayer: {
        features: featureCollection([]),
        selectedIndexes: [],
      },
      redoStack: [],
      undoStack: [],
      transforming: false,
    })
    setPreviewFeatures(featureCollection([]))
  }, [])

  const autofixOverlaps = () => {
    const overlaps = findEditorOverlaps({
      featureCollection: features as FeatureCollection<Polygon | MultiPolygon>,
      testShapes: overlapTestFields as Feature<any, { boundingBox: Array2d }>[],
    })

    if (!overlaps) return

    let newGeometry: Feature | null = mergeFeatureCollection(
      editState.editLayer.features
    ) as Feature<Polygon | MultiPolygon, any>

    overlaps.forEach(overlapFeature => {
      const subtrahend = buffer(overlapFeature, 1 / 1000)

      const diff = difference(newGeometry as Feature<Polygon | MultiPolygon>, subtrahend.geometry)

      newGeometry = diff
    })

    truncate(newGeometry as Feature<Polygon | MultiPolygon, any>, { precision: 6, mutate: true })
    const deduplicated = simplify(cleanCoords(newGeometry), { tolerance: 0.000001 })

    setEditState({
      ...editState,
      editLayer: {
        features: featureCollection([deduplicated]) as FeatureCollection<Polygon | MultiPolygon>,
        selectedIndexes: [],
      },
      undoStack: [editState.editLayer, ...editState.undoStack],
    })
    setPreviewFeatures(
      featureCollection([deduplicated]) as FeatureCollection<Polygon | MultiPolygon>
    )
  }

  return (
    <GeometryEditorStateContext.Provider
      value={{
        autofixOverlaps,
        container,
        editorOverlaps,
        editState,
        features,
        fieldShapes,
        fieldPoints,
        handleUndo,
        handleRedo,
        majorityCrops,
        resourceId,
        setEditState,
        setViewport,
        touched: editState.undoStack.length > 0,
        baseFeatureCollection,
        setBaseFeatureCollection,
        resetGeometryEditor,
        previewFeatures,
        setPreviewFeatures,
      }}
    >
      {children}
    </GeometryEditorStateContext.Provider>
  )
}

export const useGeometryEditorStateContext = () => useContext(GeometryEditorStateContext)
